import React, { useState, useEffect } from 'react';
import { Search, Wallet, CheckCircle, Clock, ArrowRightCircle, X, Save } from 'lucide-react';
import SummaryApi from '../../common';
import { toast } from 'react-toastify';
import useBank from '../../hooks/useBank';
import fetchWithAuth from '../../common/fetchWithAuth';

const UdharManagement = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [banks, setBanks] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [settleData, setSettleData] = useState({
        name: '',
        isSettled: false,
        paidIn: '',
        bankId: '',
        mode: 'Cash',
        referenceNo: '',
        note: '',
        amount: ''
    });

    const { getBankDetails } = useBank();

    const fetchUdhar = async () => {
        setLoading(true);
        try {
            const response = await fetchWithAuth(SummaryApi.getAllUdhar.url, {
                method: SummaryApi.getAllUdhar.method
            });
            const data = await response.json();
            if (data.success) {
                setTransactions(data.data);
            } else {
                console.log(`error fetching data ${data.message}`);
                toast.error(`error fetching data ${data.message}`);
            }
        } catch (error) {
            console.error("Error fetching Udhar:", error);
            // toast.error("Failed to load transactions");
        } finally {
            setLoading(false);
        }
    }

    const fetchBanks = async () => {
        try {
            const data = await getBankDetails();
            if (data.success) {
                setBanks(data.data || []);
            } else if (data.banks) {
                setBanks(data.banks);
            }
        } catch (error) {
            console.error("Error fetching banks:", error);
            toast.error("Failed to load bank accounts");
        }
    };

    const [searchTerm, setSearchTerm] = useState('');

    const filteredData = transactions.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.txnRef.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        fetchUdhar();
        fetchBanks();
    }, []);

    const totalPending = transactions.reduce((acc, curr) => acc + curr.dueAmount, 0);
    const totalCases = transactions.length;

    const openSettleModal = (data) => {
        setSelectedItem(data);
        setSettleData({
            name: data.name,
            paidIn: data.dueAmount,
            bankId: '',
            mode: 'Cash',
            referenceNo: '',
            note: '',
            amount: data.totalAmount,
            isSettled: data.isSettled,

        });
        setShowModal(true);
    };

    const handleSettleSubmit = async (e) => {
        e.preventDefault();

        if (!settleData.paidIn || !settleData.bankId) {
            toast.error("Please fill in all required fields (Amount & Bank)");
            return;
        }

        try {
            const payLoad = {
                name: settleData.name,
                paidIn: Number(settleData.paidIn),
                amount: Number(settleData.paidIn),
                udhar: 0,
                bankId: settleData.bankId,
                mode: settleData.mode,
                note: settleData.note,
                category: 'OldDebt',
                oldTransactionId: selectedItem._id,

            };

            if (settleData.referenceNo) {
                payLoad.note = `${payLoad.note ? payLoad.note + ' | ' : ''}Ref: ${settleData.referenceNo}`;
            }

            const response = await fetchWithAuth(SummaryApi.addDailyTransaction.url, {
                method: SummaryApi.addDailyTransaction.method,
                body: JSON.stringify(payLoad)
            });

            const data = await response.json();

            if (response.ok) {
                toast.success("Payment recorded successfully!");
                setShowModal(false);
                fetchUdhar(); // Refresh the list to show updated due amounts
            } else {
                console.error("Settlement Failed:", data);
                toast.error(data.message || "Failed to record payment");
            }

        } catch (error) {
            console.error("Error submitting settlement:", error);
            toast.error("Something went wrong. Please try again.");
        }
    };
    console.log(banks);
    return (
        <div className="w-full pb-10">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                    <span className="p-2 bg-amber-100 rounded-xl text-amber-600">
                        <Wallet size={32} />
                    </span>
                    Udhar (Credit) Management
                </h1>
                <p className="text-gray-500 text-sm mt-2 ml-1">
                    Track and settle outstanding customer credits.
                </p>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl border border-amber-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Total Outstanding (UDHAR)</p>
                        <h2 className="text-3xl font-bold text-amber-600">₹{totalPending.toLocaleString()}</h2>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-full text-amber-500">
                        <Clock size={24} />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Pending Cases</p>
                        <h2 className="text-3xl font-bold text-gray-800">{totalCases}</h2>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-full text-gray-500">
                        <Wallet size={24} />
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

                {/* Toolbar */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
                        <input
                            placeholder="Search by Name or Bill No..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer Name</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Bill No</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Amount</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-amber-600 uppercase tracking-wider">Pending (Udhar)</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <div className="flex justify-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                                        </div>
                                        <p className="text-gray-400 mt-2 text-sm">Loading transactions...</p>
                                    </td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                                        No records found matching your search.
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((item) => (
                                    <tr key={item._id} className="hover:bg-amber-50/30 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(item.createdAt).toLocaleDateString('en-GB')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-medium text-gray-900">{item.name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-xs">
                                            {item.txnRef}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600 font-medium">
                                            ₹{item.totalAmount.toLocaleString('INR')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-amber-600">
                                            ₹{item.dueAmount.toLocaleString('INR')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold border ${item.status === 'Partial' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-amber-50 text-amber-700 border-amber-100'
                                                }`}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <button
                                                onClick={() => openSettleModal(item)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg shadow-sm hover:bg-emerald-700 transition-all"
                                            >
                                                <CheckCircle size={14} />
                                                Settle / Pay
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Settle Modal */}
            {showModal && selectedItem && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-[450px] max-w-full border border-gray-100">
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">Settle Udhar</h3>
                                <p className="text-sm text-amber-600 font-medium">{selectedItem.name}</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSettleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Amount <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <span className="absolute left-4 top-2.5 text-gray-500 font-medium">₹</span>
                                    <input
                                        type="number"
                                        value={settleData.paidIn}
                                        onChange={(e) => setSettleData({ ...settleData, paidIn: e.target.value })}
                                        max={selectedItem.dueAmount}
                                        min={1}
                                        className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all font-bold text-gray-800"
                                        required
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1 ml-1">
                                    Current Due: <span className="font-bold text-amber-600">₹{selectedItem.dueAmount}</span>
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Mode <span className="text-red-500">*</span></label>
                                <select
                                    value={settleData.mode}
                                    onChange={(e) => setSettleData({ ...settleData, mode: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all appearance-none bg-white"
                                    required
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Check">Check</option>

                                </select>

                            </div>

                            {(settleData.mode === 'UPI' || settleData.mode === 'Check') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        {settleData.mode === 'Check' ? 'Check Number' : 'Transaction Reference'}
                                    </label>
                                    <input
                                        type="text"
                                        value={settleData.referenceNo}
                                        onChange={(e) => setSettleData({ ...settleData, referenceNo: e.target.value })}
                                        placeholder={settleData.mode === 'Check' ? "Enter Check No" : "Enter UPI Ref ID"}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Deposit to Account <span className="text-red-500">*</span> </label>
                                <select
                                    value={settleData.bankId}
                                    onChange={(e) => setSettleData({ ...settleData, bankId: e.target.value })}
                                    required
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all appearance-none bg-white"
                                >
                                    <option value="" disable className="hidden">Select Bank / Cash Account</option>
                                    {banks.map(bank => (
                                        <option key={bank._id} value={bank._id}>
                                            {bank.accountName} - ₹{bank.balance?.toLocaleString()}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Note (Optional)</label>
                                <textarea
                                    value={settleData.note}
                                    onChange={(e) => setSettleData({ ...settleData, note: e.target.value })}
                                    rows="2"
                                    placeholder="e.g. Paid by UPI / Cheque No..."
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all resize-none"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-2 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 font-bold transition-all flex items-center gap-2"
                                >
                                    <Save size={18} />
                                    Confirm Payment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UdharManagement;
